import { Link } from "react-router-dom";
import { ArrowRight, Bot, Compass, Mail, Video } from "lucide-react";
import PageHead from "@/components/PageHead";
import SharedFooter from "@/components/SharedFooter";
import WeKitLogo from "@/components/WeKitLogo";

const RESOURCES = [
  {
    to: "/free/career-quiz?src=free-resources",
    icon: Compass,
    title: "Free Career Discovery Quiz",
    blurb: "Eight questions from our real assessment. See how you naturally work and three directions worth a look.",
    meta: "About 3 minutes",
    live: true,
  },
  {
    icon: Bot,
    title: "AI Readiness Quiz",
    blurb: "How ready is your work for the next ten years of AI? A short, honest look.",
    meta: "Opening soon",
    live: false,
  },
  {
    icon: Mail,
    title: "Weekly Future Ready Newsletter",
    blurb: "One useful thing a week — a skill, a role, a way in. No noise.",
    meta: "Opening soon",
    live: false,
  },
  {
    icon: Video,
    title: "Career Clarity Webinar",
    blurb: "A live walk through how careers are actually chosen, for students and parents.",
    meta: "Opening soon",
    live: false,
  },
] as const;

export default function FreeHub() {
  return (
    <div className="min-h-dvh bg-background">
      <PageHead
        title="Free Career Resources | WeKIT™ Career Clarity 360"
        description="Start free: a short career discovery quiz built from our real assessment, plus guides and sessions for students, professionals and parents."
        path="/free"
      />

      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-4xl items-center gap-3 px-4">
          <Link to="/" className="flex items-center gap-2.5" aria-label="WeKIT home">
            <WeKitLogo size="sm" clickable={false} />
            <span className="font-display text-base font-bold tracking-tight">WeKIT™</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-4xl px-4 pb-20 pt-10 lg:pt-16">
        <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
          Start here
        </p>
        <h1 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
          Future ready starts here
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          Pick one free thing and begin. Nothing here scores you or sorts you —
          it shows you what you lean towards today, and where that could go next.
        </p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {RESOURCES.map((r) => {
            const inner = (
              <>
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/12 text-primary">
                  <r.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="mt-4 block font-display text-lg font-semibold tracking-tight">
                  {r.title}
                </span>
                <span className="mt-2 block text-sm leading-relaxed text-muted-foreground">
                  {r.blurb}
                </span>
                <span className="mt-4 flex items-center gap-1.5 text-sm font-medium text-primary">
                  {r.live ? (
                    <>
                      Start now <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </>
                  ) : (
                    <span className="text-muted-foreground">{r.meta}</span>
                  )}
                </span>
                {r.live && (
                  <span className="mt-1 block text-xs text-muted-foreground">{r.meta}</span>
                )}
              </>
            );

            return r.live && r.to ? (
              <Link
                key={r.title}
                to={r.to}
                className="rounded-3xl border border-border/60 bg-[hsl(var(--surface))] p-6 transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {inner}
              </Link>
            ) : (
              <div
                key={r.title}
                className="rounded-3xl border border-dashed border-border/50 p-6 opacity-80"
              >
                {inner}
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-3xl border border-border/60 p-6">
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Already know you want the full picture?
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Career Clarity 360™ looks at how you work, what you can take, and what you want —
            then explains every career it puts in front of you, and why.
          </p>
          <Link
            to="/payment"
            className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-2xl bg-primary px-5 text-sm font-semibold text-primary-foreground"
          >
            See the full programme <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
}
